import type { Course, CustomCourse, Direction, Lesson, Sentence, VocabBook, VocabWord } from './types';

export const DIRECTIONS: Array<{
  key: Direction;
  title: string;
  short: string;
  description: string;
  emoji: string;
  gradient: string;
}> = [
  {
    key: 'beginner',
    title: '零基础入门',
    short: '入门',
    description: '从最基础的主谓宾开始，把常见句型练熟',
    emoji: '🌱',
    gradient: 'from-mint-400 to-honey-400',
  },
  {
    key: 'exam',
    title: '升学考试',
    short: '考试',
    description: '中高考、四六级、考研的高频句型和必背词',
    emoji: '🎓',
    gradient: 'from-honey-400 to-ember-500',
  },
  {
    key: 'business',
    title: '职场商务',
    short: '商务',
    description: '写邮件、开会、谈判 — 写得专业，说得得体',
    emoji: '💼',
    gradient: 'from-honey-500 to-mint-500',
  },
  {
    key: 'overseas',
    title: '出国留学',
    short: '留学',
    description: '雅思托福高频句 + 校园里真正会用到的生存口语',
    emoji: '✈️',
    gradient: 'from-mint-500 to-honey-500',
  },
  {
    key: 'interest',
    title: '兴趣口语',
    short: '兴趣',
    description: '美剧、电影、播客台词，让英语过得跟母语一样轻松',
    emoji: '🎬',
    gradient: 'from-ember-400 to-honey-400',
  },
  {
    key: 'k12',
    title: 'K12 教材',
    short: '教材',
    description: '人教版 / 外研版 / 北师大版课文同步精讲',
    emoji: '🏫',
    gradient: 'from-honey-400 to-mint-500',
  },
  {
    key: 'custom',
    title: '我的私人课',
    short: '自创',
    description: '粘贴任意中英句对，5 秒生成可玩课程',
    emoji: '🎯',
    gradient: 'from-mint-500 to-ember-500',
  },
];

const S = (id: string, en: string, zh: string): Sentence => ({ id, en, zh });

const L = (id: string, courseId: string, title: string, sentences: Sentence[]): Lesson => ({
  id,
  courseId,
  title,
  sentences,
});

export const COURSES: Course[] = [
  // —— 零基础入门 ——
  {
    id: 'c-beg-1',
    title: '主谓宾的世界',
    description: '5 节课走完最常用的基础句型，告别"中式英语"',
    direction: 'beginner',
    emoji: '🌱',
    difficulty: 1,
    estimatedMinutes: 75,
    lessons: [
      L('l-beg-1-1', 'c-beg-1', '我是 / 你是 / 他是', [
        S('s-beg-1-1-1', 'I am a student.', '我是一名学生。'),
        S('s-beg-1-1-2', 'She is my best friend.', '她是我最好的朋友。'),
        S('s-beg-1-1-3', 'They are from Canada.', '他们来自加拿大。'),
        S('s-beg-1-1-4', 'This is a great idea.', '这是个好主意。'),
        S('s-beg-1-1-5', 'You are right.', '你是对的。'),
      ]),
      L('l-beg-1-2', 'c-beg-1', '动作开始 — 动词三态', [
        S('s-beg-1-2-1', 'I drink coffee every morning.', '我每天早上喝咖啡。'),
        S('s-beg-1-2-2', 'He works at a bank.', '他在银行工作。'),
        S('s-beg-1-2-3', 'We watched a movie last night.', '我们昨晚看了部电影。'),
        S('s-beg-1-2-4', 'She has finished her homework.', '她已经完成作业了。'),
      ]),
      L('l-beg-1-3', 'c-beg-1', '提问的句式', [
        S('s-beg-1-3-1', 'What is your name?', '你叫什么名字？'),
        S('s-beg-1-3-2', 'Where do you live?', '你住在哪里？'),
        S('s-beg-1-3-3', 'How was your weekend?', '你的周末过得怎么样？'),
      ]),
    ],
  },
  {
    id: 'c-beg-2',
    title: '日常 50 句',
    description: '点餐、问路、购物、闲聊 — 走到哪都能用',
    direction: 'beginner',
    emoji: '☕',
    difficulty: 1,
    estimatedMinutes: 60,
    lessons: [
      L('l-beg-2-1', 'c-beg-2', '咖啡馆点单', [
        S('s-beg-2-1-1', 'A latte to go, please.', '请来一杯拿铁，带走。'),
        S('s-beg-2-1-2', 'Could I have it with less sugar?', '能少放点糖吗？'),
        S('s-beg-2-1-3', 'Do you take card?', '你们收卡吗？'),
      ]),
      L('l-beg-2-2', 'c-beg-2', '问路与方向', [
        S('s-beg-2-2-1', 'Excuse me, how do I get to the station?', '请问，去车站怎么走？'),
        S('s-beg-2-2-2', 'It is just around the corner.', '就在拐角处。'),
        S('s-beg-2-2-3', 'You can take the next left.', '你可以下一个路口左转。'),
      ]),
    ],
  },

  // —— 升学考试 ——
  {
    id: 'c-exam-1',
    title: '高考高分句型 30 例',
    description: '阅卷老师眼中的"亮点句"，写作直接套用',
    direction: 'exam',
    emoji: '📚',
    difficulty: 3,
    estimatedMinutes: 90,
    lessons: [
      L('l-exam-1-1', 'c-exam-1', '强调句与倒装句', [
        S('s-exam-1-1-1', 'It is teamwork that matters most in modern society.', '在现代社会，最重要的是团队合作。'),
        S('s-exam-1-1-2', 'Never have I seen such a stunning view.', '我从未见过如此惊艳的景色。'),
        S('s-exam-1-1-3', 'Only by working together can we achieve our goal.', '只有齐心协力，我们才能实现目标。'),
      ]),
      L('l-exam-1-2', 'c-exam-1', '让步与转折', [
        S('s-exam-1-2-1', 'Despite the difficulties, we kept moving forward.', '尽管困难重重，我们仍持续前行。'),
        S('s-exam-1-2-2', 'Although she was tired, she insisted on finishing the task.', '虽然她很累，但她坚持完成了任务。'),
      ]),
    ],
  },
  {
    id: 'c-exam-2',
    title: '四六级写作模板',
    description: '开头句、过渡句、结尾句 — 全部默写级',
    direction: 'exam',
    emoji: '✍️',
    difficulty: 3,
    estimatedMinutes: 100,
    lessons: [
      L('l-exam-2-1', 'c-exam-2', '议论文开头三板斧', [
        S('s-exam-2-1-1', 'Nowadays, the issue of online privacy has aroused wide public concern.', '如今，网络隐私问题已引起公众广泛关注。'),
        S('s-exam-2-1-2', 'There is no denying that technology has reshaped our daily lives.', '不可否认，科技已重塑我们的日常生活。'),
      ]),
    ],
  },

  // —— 职场商务 ——
  {
    id: 'c-biz-1',
    title: '商务邮件 · 你来我往',
    description: '从开场白到落款，每一封邮件都得体',
    direction: 'business',
    emoji: '📧',
    difficulty: 3,
    estimatedMinutes: 80,
    lessons: [
      L('l-biz-1-1', 'c-biz-1', '开场与请求', [
        S('s-biz-1-1-1', 'I hope this email finds you well.', '愿邮件到达时您一切安好。'),
        S('s-biz-1-1-2', 'Could you kindly share the latest report with me?', '能否方便分享一下最新的报告？'),
        S('s-biz-1-1-3', 'Please let me know if any further information is needed.', '若需要任何补充信息，请告知。'),
      ]),
      L('l-biz-1-2', 'c-biz-1', '会议安排与跟进', [
        S('s-biz-1-2-1', 'Would Thursday at 3 PM work for you?', '周四下午 3 点您方便吗？'),
        S('s-biz-1-2-2', 'I am following up on the proposal we discussed last week.', '我跟进一下我们上周讨论的提案。'),
      ]),
    ],
  },
  {
    id: 'c-biz-2',
    title: '会议英语 · 听说自如',
    description: '主持、汇报、反驳 — 在会议室拿回话语权',
    direction: 'business',
    emoji: '💬',
    difficulty: 4,
    estimatedMinutes: 90,
    lessons: [
      L('l-biz-2-1', 'c-biz-2', '主持开场', [
        S('s-biz-2-1-1', "Let's get started, shall we?", '我们开始吧？'),
        S('s-biz-2-1-2', 'Today we will be covering three main topics.', '今天我们会涵盖三个主要议题。'),
        S('s-biz-2-1-3', 'Feel free to jump in if you have questions.', '有问题随时打断。'),
      ]),
    ],
  },

  // —— 出国留学 ——
  {
    id: 'c-ovs-1',
    title: '雅思口语 Part 2 万能模板',
    description: 'Topic Card 拆解 + 高分句式',
    direction: 'overseas',
    emoji: '🛫',
    difficulty: 4,
    estimatedMinutes: 120,
    lessons: [
      L('l-ovs-1-1', 'c-ovs-1', '描述一个人', [
        S('s-ovs-1-1-1', 'The person I would like to talk about is my grandmother.', '我想谈的人是我的祖母。'),
        S('s-ovs-1-1-2', 'She has had a profound influence on my life.', '她对我的人生影响深远。'),
        S('s-ovs-1-1-3', 'What strikes me the most is her resilience.', '最打动我的是她的韧性。'),
      ]),
    ],
  },
  {
    id: 'c-ovs-2',
    title: '校园生存 100 句',
    description: '租房、银行、医院、教授 office hour 全场景',
    direction: 'overseas',
    emoji: '🏫',
    difficulty: 3,
    estimatedMinutes: 110,
    lessons: [
      L('l-ovs-2-1', 'c-ovs-2', '租房沟通', [
        S('s-ovs-2-1-1', 'Is the apartment still available?', '这间公寓还能租吗？'),
        S('s-ovs-2-1-2', 'Are utilities included in the rent?', '水电费是否包含在租金里？'),
      ]),
    ],
  },

  // —— 兴趣口语 ——
  {
    id: 'c-int-1',
    title: '老友记金句',
    description: '从 "How you doin\'?" 学到地道幽默',
    direction: 'interest',
    emoji: '☕',
    difficulty: 2,
    estimatedMinutes: 60,
    lessons: [
      L('l-int-1-1', 'c-int-1', 'Chandler 式吐槽', [
        S('s-int-1-1-1', 'Could I BE any more confused?', '我还能更困惑一点吗？'),
        S('s-int-1-1-2', 'I am not great at the advice. Can I interest you in a sarcastic comment?', '建议我不擅长，要不来点讽刺评论？'),
      ]),
    ],
  },
  {
    id: 'c-int-2',
    title: '电影预告片台词',
    description: '一句话点燃整部电影 — 学高燃表达',
    direction: 'interest',
    emoji: '🎬',
    difficulty: 3,
    estimatedMinutes: 50,
    lessons: [
      L('l-int-2-1', 'c-int-2', '热血开场白', [
        S('s-int-2-1-1', 'In a world where nothing is what it seems...', '在一个一切都并非表面所见的世界里……'),
        S('s-int-2-1-2', 'They thought it was over. They were wrong.', '他们以为一切结束了。他们错了。'),
      ]),
    ],
  },

  // —— K12 教材联动 ——
  {
    id: 'c-k12-rj',
    title: '人教版 · 八年级上 Unit 1',
    description: 'Where did you go on vacation? — 课文同步',
    direction: 'k12',
    emoji: '📕',
    difficulty: 2,
    estimatedMinutes: 50,
    lessons: [
      L('l-k12-rj-1', 'c-k12-rj', 'Section A · Talk about vacations', [
        S('s-k12-rj-1-1', 'Where did you go on vacation?', '你假期去哪儿了？'),
        S('s-k12-rj-1-2', 'I went to summer camp.', '我去了夏令营。'),
        S('s-k12-rj-1-3', 'Did anyone come with you?', '有人和你一起去吗？'),
        S('s-k12-rj-1-4', 'No, no one was here. Everyone was on vacation.', '不，没人在这儿。大家都在度假。'),
      ]),
      L('l-k12-rj-2', 'c-k12-rj', 'Section B · A trip diary', [
        S('s-k12-rj-2-1', 'The food tasted really good.', '食物尝起来很美味。'),
        S('s-k12-rj-2-2', 'We had great fun playing in the water.', '我们在水里玩得非常开心。'),
      ]),
    ],
  },
  {
    id: 'c-k12-wy',
    title: '外研版 · 七年级下 Module 3',
    description: 'Making plans · 课文 + 拓展表达',
    direction: 'k12',
    emoji: '📗',
    difficulty: 2,
    estimatedMinutes: 45,
    lessons: [
      L('l-k12-wy-1', 'c-k12-wy', 'Unit 1 · What are you going to do?', [
        S('s-k12-wy-1-1', "I'm going to the cinema this weekend.", '我这个周末打算去看电影。'),
        S('s-k12-wy-1-2', 'What are you going to do on Sunday?', '你周日打算做什么？'),
        S('s-k12-wy-1-3', "I'm going to visit my grandparents.", '我打算去看望我的祖父母。'),
      ]),
    ],
  },
  {
    id: 'c-k12-bs',
    title: '北师大版 · 高一上 Unit 2',
    description: 'Sports and fitness — 高频表达',
    direction: 'k12',
    emoji: '📘',
    difficulty: 3,
    estimatedMinutes: 60,
    lessons: [
      L('l-k12-bs-1', 'c-k12-bs', 'Lesson 1 · Extreme sports', [
        S('s-k12-bs-1-1', "He's a great athlete who always pushes himself.", '他是一位永远在突破自我的伟大运动员。'),
        S('s-k12-bs-1-2', 'Sports help us stay healthy both physically and mentally.', '运动帮助我们在身心两方面保持健康。'),
      ]),
    ],
  },
];

export const COURSE_BY_ID: Record<string, Course> = Object.fromEntries(
  COURSES.map((c) => [c.id, c]),
);

export const LESSON_BY_ID: Record<string, Lesson> = Object.fromEntries(
  COURSES.flatMap((c) => c.lessons).map((l) => [l.id, l]),
);

export const SENTENCE_BY_ID: Record<string, Sentence & { lessonId: string; courseId: string }> =
  Object.fromEntries(
    COURSES.flatMap((c) =>
      c.lessons.flatMap((l) =>
        l.sentences.map((s) => [s.id, { ...s, lessonId: l.id, courseId: c.id }] as const),
      ),
    ),
  );

/** Resolve a lesson by ID, falling back to a user's custom course. */
export function resolveLesson(
  id: string,
  customs: CustomCourse[],
): { lesson: Lesson; course: Course } | null {
  const stat = LESSON_BY_ID[id];
  if (stat !== undefined) {
    const course = COURSE_BY_ID[stat.courseId];
    if (course !== undefined) return { lesson: stat, course };
  }
  const cc = customs.find((c) => c.id === id);
  if (cc !== undefined) {
    const lesson: Lesson = { id: cc.id, courseId: cc.id, title: cc.title, sentences: cc.sentences };
    const course: Course = {
      id: cc.id,
      title: cc.title,
      description: cc.description,
      direction: 'custom' as Direction,
      emoji: cc.emoji,
      difficulty: 2,
      estimatedMinutes: cc.sentences.length * 2,
      lessons: [lesson],
    };
    return { lesson, course };
  }
  return null;
}

// ——————————————————————————————————————————————
// Vocabulary
// ——————————————————————————————————————————————

const W = (
  id: string,
  en: string,
  zh: string,
  ipa: string,
  pos: string,
  exampleEn: string,
  exampleZh: string,
  imageEmoji: string,
): VocabWord => ({ id, en, zh, ipa, pos, exampleEn, exampleZh, imageEmoji });

export const VOCAB: VocabWord[] = [
  W('w-1', 'resilience', '韧性，恢复力', '/rɪˈzɪliəns/', 'n.', 'Her resilience inspires everyone around her.', '她的韧性激励着身边每一个人。', '🌳'),
  W('w-2', 'profound', '深远的，深刻的', '/prəˈfaʊnd/', 'adj.', 'The book had a profound impact on me.', '这本书对我影响深远。', '🌊'),
  W('w-3', 'embrace', '拥抱，欣然接受', '/ɪmˈbreɪs/', 'v.', 'We should embrace change instead of fearing it.', '我们应当拥抱变化，而非畏惧。', '🤗'),
  W('w-4', 'ambiguous', '模糊的，含糊的', '/æmˈbɪɡjuəs/', 'adj.', 'The instructions were ambiguous.', '指令含糊不清。', '🌫️'),
  W('w-5', 'inevitable', '不可避免的', '/ɪnˈevɪtəbl/', 'adj.', 'Some setbacks are inevitable.', '有些挫折是不可避免的。', '⏳'),
  W('w-6', 'collaborate', '协作', '/kəˈlæbəreɪt/', 'v.', 'Designers and engineers collaborate closely.', '设计师和工程师密切协作。', '🤝'),
  W('w-7', 'sustain', '维持，持续', '/səˈsteɪn/', 'v.', 'Habits sustain long-term progress.', '习惯能维持长期进步。', '🌱'),
  W('w-8', 'pivotal', '关键的', '/ˈpɪvətl/', 'adj.', 'This is a pivotal moment in her career.', '这是她职业生涯的关键时刻。', '🎯'),
  W('w-9', 'mundane', '平凡的', '/mʌnˈdeɪn/', 'adj.', 'Even mundane tasks deserve attention.', '即便是琐碎之事也值得关注。', '🧺'),
  W('w-10', 'eloquent', '雄辩的，有说服力的', '/ˈeləkwənt/', 'adj.', 'She gave an eloquent speech.', '她做了一场雄辩的演讲。', '🗣️'),
  W('w-11', 'curate', '策划，精选', '/ˈkjʊəreɪt/', 'v.', 'He curates a weekly reading list.', '他每周精选阅读清单。', '📚'),
  W('w-12', 'paradigm', '范式', '/ˈpærədaɪm/', 'n.', 'This invention shifted the paradigm.', '这项发明改变了范式。', '🔭'),
  W('w-13', 'leverage', '利用，撬动', '/ˈlevərɪdʒ/', 'v.', 'Leverage your strengths.', '善用你的优势。', '⚖️'),
  W('w-14', 'venture', '冒险，企业', '/ˈventʃər/', 'n./v.', 'They launched a new venture.', '他们开启了一项新事业。', '🚀'),
  W('w-15', 'cultivate', '培养', '/ˈkʌltɪveɪt/', 'v.', 'Cultivate the habit of reading daily.', '培养每日阅读的习惯。', '🌷'),
  W('w-16', 'tangible', '有形的，具体的', '/ˈtændʒəbl/', 'adj.', 'We need tangible results.', '我们需要具体的成果。', '🧱'),
  W('w-17', 'novel', '新颖的', '/ˈnɒvl/', 'adj.', 'A novel approach to an old problem.', '老问题的新解法。', '💡'),
  W('w-18', 'foster', '促进，培养', '/ˈfɒstər/', 'v.', 'Open dialogue fosters trust.', '开诚布公能促进信任。', '🌼'),
  W('w-19', 'rigorous', '严格的，严谨的', '/ˈrɪɡərəs/', 'adj.', 'They follow a rigorous process.', '他们遵循严谨的流程。', '🔬'),
  W('w-20', 'intuitive', '直观的，凭直觉的', '/ɪnˈtjuːɪtɪv/', 'adj.', 'The interface is intuitive.', '界面非常直观。', '✨'),
  W('w-21', 'concise', '简洁的', '/kənˈsaɪs/', 'adj.', 'Keep your writing concise.', '写作保持简洁。', '✂️'),
  W('w-22', 'navigate', '导航，应对', '/ˈnævɪɡeɪt/', 'v.', 'How do we navigate uncertainty?', '我们如何应对不确定性？', '🧭'),
  W('w-23', 'inherent', '固有的', '/ɪnˈherənt/', 'adj.', 'Trust is inherent to good teams.', '信任是优秀团队的固有要素。', '🧬'),
  W('w-24', 'augment', '增强', '/ɔːɡˈment/', 'v.', 'AI augments human creativity.', 'AI 增强人类创造力。', '⚡'),
  W('w-25', 'discern', '辨别', '/dɪˈsɜːrn/', 'v.', 'Learn to discern signal from noise.', '学会从噪音中辨别信号。', '🔎'),
  W('w-26', 'pragmatic', '务实的', '/præɡˈmætɪk/', 'adj.', 'A pragmatic approach often wins.', '务实的方法往往制胜。', '🛠️'),
  W('w-27', 'compelling', '引人入胜的', '/kəmˈpelɪŋ/', 'adj.', 'She made a compelling argument.', '她提出了一个令人信服的论点。', '🎙️'),
  W('w-28', 'iterate', '迭代', '/ˈɪtəreɪt/', 'v.', 'We iterate quickly on prototypes.', '我们对原型快速迭代。', '🔁'),
  W('w-29', 'meticulous', '一丝不苟的', '/məˈtɪkjələs/', 'adj.', 'Meticulous attention to detail.', '对细节一丝不苟。', '🧷'),
  W('w-30', 'galvanize', '激励', '/ˈɡælvənaɪz/', 'v.', 'The speech galvanized the audience.', '演讲激励了观众。', '⚡'),
];

export const VOCAB_BY_ID: Record<string, VocabWord> = Object.fromEntries(
  VOCAB.map((w) => [w.id, w]),
);

export const VOCAB_BOOKS: VocabBook[] = [
  {
    id: 'vb-1',
    title: '雅思高频 300',
    description: '雅思口语写作里反复出现的词，背了立刻就能用',
    emoji: '🎯',
    wordIds: ['w-1', 'w-2', 'w-4', 'w-5', 'w-8', 'w-9', 'w-10', 'w-12', 'w-19', 'w-29'],
  },
  {
    id: 'vb-2',
    title: '职场表达精选',
    description: '写邮件、做汇报、谈合作绕不开的动词',
    emoji: '💼',
    wordIds: ['w-3', 'w-6', 'w-7', 'w-13', 'w-15', 'w-18', 'w-22', 'w-24', 'w-26', 'w-28'],
  },
  {
    id: 'vb-3',
    title: '思辨写作词',
    description: '议论文常用的关键形容词，论证更有力',
    emoji: '🧠',
    wordIds: ['w-11', 'w-14', 'w-16', 'w-17', 'w-20', 'w-21', 'w-23', 'w-25', 'w-27', 'w-30'],
  },
];
